import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  BugAntIcon,
  RectangleStackIcon,
  XMarkIcon,
  UserIcon,
  UserPlusIcon
  , ScaleIcon
} from '@heroicons/react/20/solid'
import clsx from 'clsx'
import {
  Pill,
  PSmallStrong
} from './Type'
import { Card } from './Cards'
import { useApiQuery } from '../../utils/api'

// Type definitions
interface User {
  id: string
  name: string
  /** Some API payloads return `displayName` instead of `name` */
  displayName?: string
  avatar?: string
}

interface EpicColor {
  key: string
  [key: string]: unknown // Replaced 'any' with 'unknown'
}

interface Epic {
  id?: number
  key?: string
  name: string
  color?: EpicColor
  [key: string]: unknown // Replaced 'any' with 'unknown'
}

interface StatusObject {
  name: string
  id?: string
  statusCategoryKey?: 'new' | 'indeterminate' | 'done'
  iconUrl?: string
}

interface Issue {
  id: string
  key: string
  type: string
  summary: string
  status?: string | StatusObject | null // Updated to allow string, object, or null/undefined
  epic?: Epic
  owner?: User | null
  creator?: User
  estimate?: number | null
  startEstimate?: number
  flagged: boolean
  startedWith?: boolean
  resolution?: string
  isPreviouslyReleased?: boolean
  [key: string]: unknown // Replaced 'any' with 'unknown'
}

export interface Category {
  name: string
  issues: Issue[]
}

interface AddedByResponse {
  key: string
  addedByName: string
  addedById: string
}

interface IssueStatusResponse {
  status?: number
  state?: string
  message?: string
}

interface IssueIconProps {
  type: string
}

interface IssueCardProps {
  issue: Issue
  sprintId: string
  removed?: boolean
  category?: string // Added category prop to know the context
  addedByLookup?: Record<string, AddedByResponse>
  isLoadingAddedBy?: boolean
}

interface IssuesPanelProps {
  title: string
  categories: Category[]
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  sprintId: string
  /** Tailwind background color utility (e.g. `bg-red-500`). */
  headerColor?: string
  /* Added-by batching props (optional because panel may be used elsewhere) */
  addedByLookup?: Record<string, AddedByResponse>
  isLoadingAddedBy?: boolean
}

// Color mapping from Jira colors to Tailwind
const jiraToTailwindColorMap: Record<string, string> = {
  "color_1": "stone",
  "color_2": "red",
  "color_3": "yellow",
  "color_4": "green",
  "color_5": "blue",
  "color_6": "indigo",
  "color_7": "purple",
  "color_8": "pink",
  "color_9": "rose",
  "color_10": "lime",
  "color_11": "emerald",
  "color_12": "teal",
  "color_13": "cyan",
  "color_14": "sky",
  "color_15": "violet",
  "color_16": "fuchsia",
  "color_17": "amber",
  "color_18": "orange"
}

// ------------------------------------------------------------
// Utility: truncate text at word-boundary with a max length
// ------------------------------------------------------------
const truncateAtWord = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) return text

  // Slice then walk backwards to the last space, but don't cut too early
  const slice = text.slice(0, maxLength)
  const lastSpace = slice.lastIndexOf(' ')

  // If we find a space after ~70 % of maxLength, break there – else hard cut
  const cutIndex =
    lastSpace > Math.floor(maxLength * 0.7) ? lastSpace : maxLength

  return slice.slice(0, cutIndex).trimEnd() + '...'
}

export const IssueIcon: React.FC<IssueIconProps> = ({ type }) => {
  let color = 'bg-slate-700'
  switch (type) {
    case 'Bug':
      color = 'bg-red-700'
      break
    case 'Story':
      color = 'bg-green-700'
      break
    case 'Task':
      color = 'bg-blue-700'
      break
    case 'Data Changes':
      color = 'bg-purple-700'
      break
  }
  let IssueIcon = BugAntIcon
  if (type !== 'Bug') {
    IssueIcon = RectangleStackIcon
  }

  return (
    <div>
      <div className={clsx('float-right rounded-md p-1', color)}>
        <IssueIcon className='h-3 w-3 text-white' aria-hidden='true' />
      </div>
    </div>
  )
}

// Define a type for the Card component when used as an anchor
type CardAnchorProps = React.ComponentProps<typeof Card> & React.AnchorHTMLAttributes<HTMLAnchorElement>

export const IssueCard: React.FC<IssueCardProps> = ({
  issue,
  removed = false,
  category,
  addedByLookup,
  isLoadingAddedBy
}) => {
  /* ------------------------------------------------------------
   * Helpers
   * ---------------------------------------------------------- */
  const getCreatorName = (): string => {
    // 1️⃣  Preferred: explicit creator field
    if (issue.creator?.name) return issue.creator.name
    if (
      // sometimes the creator object comes with displayName only
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (issue.creator as unknown as { displayName?: string })?.displayName
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return (issue.creator as unknown as { displayName: string }).displayName
    }
    // 2️⃣  Fallback: owner information (displayName or name)
    if (
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (issue.owner as unknown as { displayName?: string })?.displayName
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return (issue.owner as unknown as { displayName: string }).displayName
    }
    if (issue.owner?.name) return issue.owner.name
    // 3️⃣  Final fallback
    return 'Unknown'
  }

  // Determine epic pill colour, falling back to gray
  let epicColor = 'gray'
  // `issue.epic.color` comes back from Jira as a string like "color_14"
  if (issue.epic && typeof issue.epic.color === 'string') {
    epicColor = jiraToTailwindColorMap[issue.epic.color] || 'gray'
  }

  const getAddedByData = (): string => {
    if (isLoadingAddedBy) {
      return "Loading..."
    }

    if (addedByLookup && addedByLookup[issue.key]) {
      return addedByLookup[issue.key].addedByName
    } else {
      return getCreatorName()
    }
  }

  // Helper function to determine if we should show the "Added by" badge
  const shouldShowAddedBy = (): boolean => {
    // In panels, only show "Added by" for issues in the "Added" category
    if (category) {
      return category === 'Added'
    }
    // On main pages, use the original logic
    return !issue.startedWith
  }

  const { data: issueStatus, isError } = useApiQuery<IssueStatusResponse>(
    `/issues/${issue.id}/status`,
    { enabled: removed, retry: false }
  )

  let removedLabel = 'Removed'
  let removedColor = ''

  if (isError) {
    removedLabel = 'Error'
    removedColor = 'red-900'
  } else if (issueStatus && issueStatus.status === 404) {
    removedLabel = 'Deleted'
    removedColor = 'red-500'
  } else if (issueStatus && issueStatus.state) {
    removedLabel = 'Removed'
    removedColor = 'orange-500'
  }

  // Use statusCategoryKey for pill color (locale-independent)
  const statusCategoryKey = (typeof issue.status === 'object' && issue.status?.statusCategoryKey)
    ? issue.status.statusCategoryKey
    : undefined

  // Normalise status display text — fall back to English category label
  // when the Jira-provided name is in a non-Latin locale (e.g. "已完成")
  const rawStatusText = (typeof issue.status === 'object' && issue.status?.name)
    ? issue.status.name
    : (typeof issue.status === 'string' ? issue.status : '')
  const STATUS_CATEGORY_LABELS: Record<string, string> = {
    done: 'Done',
    new: 'To Do',
    indeterminate: 'In Progress',
  }
  const isNonLatin = /[^\u0000-\u024F]/.test(rawStatusText)
  const statusText = (isNonLatin && statusCategoryKey && STATUS_CATEGORY_LABELS[statusCategoryKey])
    ? STATUS_CATEGORY_LABELS[statusCategoryKey]
    : rawStatusText
  const statusPillColor = statusCategoryKey === 'done' ? 'green'
    : statusCategoryKey === 'new' ? 'slate'
      : 'blue'

  return (
    <Card
      as='a'
      className={clsx(
        'relative col-span-1 border-2 p-4 text-center shadow shadow-slate-300 hover:bg-slate-50',
        issue.flagged ? 'bg-yellow-100' : 'bg-white',
        issue.startedWith ? 'border-blue-300' : 'border-orange-500',
        removed ? 'border-red-500' : ''
      )}
      href={`https://cnsgitbits.atlassian.net/browse/${issue.key}`}
      target='_blank'
      key={issue.key}
      // Using a more specific type assertion for Card's anchor props
      {...{} as CardAnchorProps}
    >
      {removed && (
        <div className={`absolute top-2 right-2 bg-${removedColor} text-white text-xs p-1 rounded px-2`}>
          <strong>{removedLabel}</strong>
        </div>
      )}
      {/* Row 1: Icon + Key + Status pill (top right) */}
      <div className='flex items-center'>
        <div className='flex items-center'>
          <div>
            <IssueIcon type={issue.type} />
          </div>
          <div className='ml-2 text-left'>
            <PSmallStrong text={issue.key} />
          </div>
        </div>
        <div className='flex-grow text-right'>
          {statusText && (
            <Pill
              text={statusText}
              color={statusPillColor}
            />
          )}
        </div>
      </div>

      {/* Row 2: Creator (left) + Added by orange text (right) */}
      <div className='flex items-center mt-2'>
        <div className='flex-grow text-left text-xs flex items-center'>
          <UserIcon className="h-3 w-3 mr-1 text-gray-500" />
          <span>{getCreatorName()}</span>
        </div>
        {shouldShowAddedBy() && (
          <div className='text-orange-500 text-xs font-semibold flex items-center'>
            <UserPlusIcon className="h-3 w-3 mr-1" />
            {getAddedByData()}
          </div>
        )}
      </div>

      {/* Row 3: Summary (with less spacing) */}
      <div className='mt-2 text-left text-sm overflow-hidden h-16'>
        {issue.summary}
      </div>

      {/* Row 4: Epic (left) + Points (right) */}
      <div className='mt-2 flex items-center'>
        <div className='flex-grow overflow-hidden text-left'>
          <Pill
            text={issue.epic ? truncateAtWord(issue.epic.name, 26) : 'No Epic'}
            color={epicColor}
          />
        </div>
        <div className={clsx(
          'text-right text-sm ml-2',
          issue.isPreviouslyReleased ? 'text-gray-300' : ''
        )}>
          <span className='inline-flex items-center space-x-1'>
            {/* Points icon */}
            <ScaleIcon className='h-4 w-4 text-slate-500 flex-shrink-0' aria-hidden='true' />
            {/* Points value(s) */}
            {issue.startEstimate !== undefined && issue.startEstimate !== null
              ? (
                // Show \"start → end\" if startEstimate exists
                <>
                  <span>{issue.startEstimate}</span>
                  <span className='mx-0.5'>→</span>
                  <span>
                    {issue.estimate !== null && issue.estimate !== undefined
                      ? issue.estimate
                      : '-'}
                  </span>
                </>
              )
              : (
                <>
                  <span>
                    {issue.estimate !== null && issue.estimate !== undefined
                      ? issue.estimate
                      : '-'}
                  </span>
                </>
              )
            }
          </span>
        </div>
      </div>
    </Card>
  )
}

export const IssuesPanel: React.FC<IssuesPanelProps> = ({
  title,
  categories,
  isOpen,
  setIsOpen,
  sprintId,
  headerColor = 'bg-gray-500',
  addedByLookup,
  isLoadingAddedBy
}) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-10'
        onClose={() => setIsOpen(false)}
      >
        <div className='fixed inset-0 bg-black/30' aria-hidden='true' />
        <div className='fixed inset-0' />

        <div className='fixed inset-0 overflow-hidden'>
          <div className='absolute inset-0 overflow-hidden'>
            <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16'>
              <Transition.Child
                as={Fragment}
                enter='transform transition ease-in-out duration-500 sm:duration-700'
                enterFrom='translate-x-full'
                enterTo='translate-x-0'
                leave='transform transition ease-in-out duration-500 sm:duration-700'
                leaveFrom='translate-x-0'
                leaveTo='translate-x-full'
              >
                <Dialog.Panel className='pointer-events-auto w-screen max-w-2xl'>
                  <div className='flex h-full flex-col overflow-y-scroll bg-white shadow-xl'>
                    {/* Header */}
                    <div className={`px-4 py-6 sm:px-6 ${headerColor}`}>
                      <div className='flex items-start justify-between'>
                        {/* slightly smaller headline so it doesn't overpower the panel */}
                        <Dialog.Title className='text-white text-2xl font-semibold'>
                          {title}
                        </Dialog.Title>
                        <div className='ml-3 flex h-7 items-center'>
                          <button
                            type='button'
                            className='rounded-full bg-white bg-opacity-20 p-2 text-white hover:bg-opacity-30 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent'
                            onClick={() => setIsOpen(false)}
                          >
                            <span className='sr-only'>Close panel</span>
                            <XMarkIcon className='h-5 w-5' aria-hidden='true' />
                          </button>
                        </div>
                      </div>
                    </div>
                    {categories.map(category => {
                      if (category.issues && category.issues.length) {
                        category.issues.sort((a, b) => (a.id > b.id ? 1 : -1))
                        return (
                          <div
                            className='relative mb-6 flex-1 px-4 sm:px-6 mt-6'
                            key={category.name}
                          >
                            <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4'>
                              {category.name}
                            </h3>
                            <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
                              {category.issues.map(issue => (
                                <IssueCard
                                  issue={issue}
                                  key={issue.key + ' - ' + category.name}
                                  sprintId={sprintId}
                                  category={category.name}
                                  addedByLookup={addedByLookup}
                                  isLoadingAddedBy={isLoadingAddedBy}
                                />
                              ))}
                            </div>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
